/** Explicit browser consent. Reuses NDG sign-in, never asks for Apple credentials. */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import "../universal_intent/UniversalIntentResponseSheet.css";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api/mail-connections/chatgpt`;
const CONNECTION = "https://ndg-backend.onrender.com/api/mail-connections/chatgpt/mcp";
const client = axios.create({ withCredentials: true, timeout: 20000,
  headers: { "X-NDG-Mail-Consent": "browser-v1", "Cache-Control": "no-store" } });
const button = "karen-panel-button";

function safeRedirect(value) {
  try {
    const url = new URL(value);
    return url.origin === "https://chatgpt.com" && !url.hash && !url.username && !url.password
      && /^\/(connector_platform_oauth_redirect|connector\/oauth\/[A-Za-z0-9_-]{1,150})$/.test(url.pathname);
  } catch { return false; }
}

function Connection({ account }) {
  const location = useLocation();
  const query = new URLSearchParams(location.hash.slice(1));
  const request = query.get("request");
  const validRequest = request && /^[A-Za-z0-9_-]{32,100}$/.test(request) && query.getAll("request").length === 1;
  const [details, setDetails] = useState(null);
  const [grants, setGrants] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [reload, setReload] = useState(0);
  const active = useRef(true);
  const generation = useRef(0);

  useEffect(() => {
    active.current = true;
    const controller = new AbortController();
    const revision = ++generation.current;
    setLoading(true); setDetails(null); setGrants([]); setError(""); setNotice("");
    if (request && !validRequest) {
      setError("This connection request is incomplete. Restart it from ChatGPT."); setLoading(false);
      return () => { active.current = false; generation.current += 1; };
    }
    client.get(request ? `${API}/consent/${request}` : `${API}/grants`, { signal: controller.signal })
      .then(({ data }) => {
        if (!active.current || revision !== generation.current) return;
        if (request) {
          if (data.scope !== "mail.read" || typeof data.nonce !== "string" || typeof data.email !== "string") throw new Error("Unexpected permission response");
          setDetails(data);
        } else if (Array.isArray(data.grants)) setGrants(data.grants);
        else throw new Error("Unexpected connection response");
      }).catch((failure) => {
        if (active.current && revision === generation.current) setError(failure?.response?.status === 401
          ? "Your NDG sign-in expired. Sign in again before connecting."
          : "This connection request could not be opened. It may have expired or the mailbox may have changed. Restart the connection from ChatGPT; do not reconnect Apple Mail.");
      }).finally(() => { if (active.current && revision === generation.current) setLoading(false); });
    return () => { active.current = false; generation.current += 1; controller.abort(); };
  }, [account, request, validRequest, reload]);

  const decide = async (approved) => {
    if (busy || !details || !validRequest) return;
    const revision = generation.current;
    setBusy(true); setError("");
    try {
      const { data } = await client.post(`${API}/consent/${request}`, { approved, nonce: details.nonce });
      if (!active.current || revision !== generation.current) return;
      if (!safeRedirect(data.redirect)) throw new Error("Unexpected callback");
      window.location.assign(data.redirect);
    } catch (_) {
      if (active.current && revision === generation.current) setError("The connection was not confirmed. Restart it from ChatGPT. Your Apple connection is unchanged.");
    } finally { if (active.current && revision === generation.current) setBusy(false); }
  };

  const revoke = async (id) => {
    if (busy) return;
    const revision = generation.current;
    setBusy(true); setError("");
    try {
      const { data } = await client.delete(`${API}/grants/${encodeURIComponent(id)}`);
      if (!active.current || revision !== generation.current) return;
      if (data.revoked !== true) throw new Error("Not confirmed");
      setGrants((current) => current.filter((grant) => grant.id !== id));
      setNotice("ChatGPT's access was revoked. NDG's own email connection is unchanged.");
    } catch (_) {
      if (active.current && revision === generation.current) setError("Revocation was not confirmed. Try again.");
    } finally { if (active.current && revision === generation.current) setBusy(false); }
  };

  return <div className="karen-email-page" data-testid="chatgpt-mail-connection">
    <header><h1>Connect ChatGPT to your email</h1><Link className={button} to="/settings/email-connections">Back to email settings</Link></header>
    <main style={{ maxWidth: "52rem", fontSize: "1.1rem", lineHeight: 1.7 }}>
      {loading && <p role="status">Checking your NDG account…</p>}
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      {details && <section data-testid="chatgpt-mail-consent">
        <h2>Allow ChatGPT to search and read?</h2>
        <p>Connected mailbox: <strong>{details.email}</strong></p>
        <p>{details.description}</p>
        <p>Your Apple password stays inside NDG. Only emails requested through this connection are returned to ChatGPT. No sending, deleting, moving or marking emails as read is granted here.</p>
        <p>You can revoke this access from this page. It expires after 30 days unless you connect again.</p>
        <div className="karen-mail-toolbar">
          <button className={button} type="button" disabled={busy} onClick={() => decide(true)}>Allow read-only access</button>
          <button className={button} type="button" disabled={busy} onClick={() => decide(false)}>Do not connect</button>
        </div>
      </section>}
      {!request && !loading && <section>
        <p>This is the separate connection for ChatGPT. Karen's access inside NDG is unchanged.</p>
        <p>Add this server in ChatGPT's custom connection settings on the web, choose OAuth, then approve the NDG permission screen. Account and workspace availability can vary.</p>
        <label htmlFor="chatgpt-mail-url">Connection URL</label>
        <input id="chatgpt-mail-url" readOnly value={CONNECTION} onFocus={(e) => e.target.select()}
          style={{ display: "block", width: "100%", padding: ".8rem", background: "transparent", color: "inherit", border: "1px solid #64748b", margin: ".5rem 0 1rem" }} />
        <button className={button} type="button" onClick={async () => {
          try { await navigator.clipboard.writeText(CONNECTION); setNotice("Connection URL copied."); }
          catch { setNotice("Tap the URL field to select and copy it."); }
        }}>Copy connection URL</button>
        <h2 style={{ marginTop: "1.5rem" }}>Authorised connections</h2>
        {grants.length === 0 ? <p>No active ChatGPT email permission is recorded.</p>
          : grants.map((grant) => <div key={grant.id} style={{ borderTop: "1px solid #64748b", padding: "1rem 0" }}>
            <p>ChatGPT · Read-only · Expires {new Date(grant.expires_at).toLocaleDateString("en-GB")}</p>
            <button className={button} type="button" disabled={busy} onClick={() => revoke(grant.id)}>Revoke this connection</button>
          </div>)}
        <button className={button} type="button" disabled={busy} onClick={() => setReload((n) => n + 1)}>Refresh connection status</button>
      </section>}
    </main>
  </div>;
}

export default function ChatGPTMailConnection() {
  const { user, loading } = useAuth();
  const account = user?.id || user?._id || "";
  if (loading) return <p role="status">Checking your NDG account…</p>;
  if (!account || !["adi", "pdi", "admin"].includes(user?.role)) return <p role="alert">Sign in to your NDG instructor account.</p>;
  return <Connection key={account} account={account} />;
}
